import { useMemo } from 'react';

import { useCurrentEmployeeContext } from '@/common/contexts/CurrentEmployeeContext';
import { EmployeeRole } from '@/common/graphql';

/**
 * Access helpers for the IDP feature. The backend enforces all rules; these
 * flags only drive UI rendering (hide controls the caller cannot use).
 *
 * Mirrors the backend's two-tier distinction (AuthUser.isAdminOrAbove() vs isHrAdmin()):
 * - `isAdminOrAbove`: SUPER_ADMIN or any ADMIN_PRIVATE, regardless of idpFullAccess - gates only
 *   the IDP list/nav entry point (043's flag-OFF ADMIN_PRIVATE users still see their own
 *   Line-Manager-scoped list).
 * - `isHrAdmin`: SUPER_ADMIN, or ADMIN_PRIVATE with idpFullAccess ON - gates every privileged
 *   action (Create, Confirm, editing Managers/auto-populated fields).
 */
export const useIdpAccess = () => {
  const { currentEmployee, loading } = useCurrentEmployeeContext();
  const role = currentEmployee?.permission?.role ?? null;
  const idpFullAccess = currentEmployee?.permission?.idpFullAccess ?? false;

  // `role` is null while the permission is still resolving; isAdminOrAbove/isHrAdmin below are
  // coerced to plain booleans (false) for that window before being returned - callers that need
  // to distinguish "still resolving" from "denied" must check the `loading` field returned below,
  // not treat a false isAdminOrAbove/isHrAdmin as a settled answer (see IdpList.tsx).
  const isAdminOrAbove = useMemo(() => {
    return role === null ? null : [EmployeeRole.SuperAdmin, EmployeeRole.AdminPrivate].includes(role);
  }, [role]);

  const isHrAdmin = useMemo(() => {
    if (role === null) return null;
    return role === EmployeeRole.SuperAdmin || (role === EmployeeRole.AdminPrivate && idpFullAccess);
  }, [role, idpFullAccess]);

  return { loading, isAdminOrAbove: isAdminOrAbove === true, isHrAdmin: isHrAdmin === true };
};
